import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, MessageSquareText, Mail, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE_DISPLAY = "(561) 705-9807";
const PHONE_TEL = "tel:+15617059807";
const PHONE_SMS = "sms:+15617059807";
const EMAIL = "shontasolutions@currently.com";

const INITIAL = { name: "", contact: "", message: "" };

export default function ContactHousing() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, message: e.detail }));
    window.addEventListener("prefill-contact", handler);
    return () => window.removeEventListener("prefill-contact", handler);
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! We received your message and will get back to you soon.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Something went wrong sending your message. Please call or text us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 sm:py-28 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="bg-[#222222] rounded-[2rem] overflow-hidden grid lg:grid-cols-2">
          <div className="p-8 sm:p-12 lg:p-16 text-white flex flex-col justify-center">
            <Reveal>
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#EDE9E1]">Contact</p>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
                Need Housing? Contact Us.
              </h2>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-white/70 max-w-lg">
                Tell us what you're looking for. We'll let you know what's available and answer
                your questions.
              </p>

              <div className="mt-8 space-y-3">
                <a
                  href={PHONE_TEL}
                  data-testid="contact-call-button"
                  className="flex items-center gap-4 bg-white/10 hover:bg-white/15 rounded-2xl px-6 py-4 transition-colors duration-200"
                >
                  <span className="w-11 h-11 rounded-xl bg-[#FF5A5F] flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </span>
                  <span>
                    <span className="block text-xs font-bold tracking-widest uppercase text-white/50">Call Us</span>
                    <span className="block font-bold">{PHONE_DISPLAY}</span>
                  </span>
                </a>
                <a
                  href={PHONE_SMS}
                  data-testid="contact-text-button"
                  className="flex items-center gap-4 bg-white/10 hover:bg-white/15 rounded-2xl px-6 py-4 transition-colors duration-200"
                >
                  <span className="w-11 h-11 rounded-xl bg-[#FF5A5F] flex items-center justify-center shrink-0">
                    <MessageSquareText className="w-5 h-5 text-white" />
                  </span>
                  <span>
                    <span className="block text-xs font-bold tracking-widest uppercase text-white/50">Text Us</span>
                    <span className="block font-bold">{PHONE_DISPLAY}</span>
                  </span>
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="contact-email-button"
                  className="flex items-center gap-4 bg-white/10 hover:bg-white/15 rounded-2xl px-6 py-4 transition-colors duration-200"
                >
                  <span className="w-11 h-11 rounded-xl bg-[#FF5A5F] flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </span>
                  <span>
                    <span className="block text-xs font-bold tracking-widest uppercase text-white/50">Email Us</span>
                    <span className="block font-bold break-all">{EMAIL}</span>
                  </span>
                </a>
              </div>
            </Reveal>
          </div>

          <div className="bg-white p-8 sm:p-12">
            <Reveal>
              <h3 className="text-2xl font-bold text-[#222]">Send a Message</h3>
              <p className="mt-2 text-sm text-[#717171]">
                We read every message and respond as soon as we can.
              </p>
              <form onSubmit={handleSubmit} className="mt-7 space-y-5" data-testid="contact-form">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Your Name</Label>
                  <Input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jane Smith"
                    className="rounded-xl h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-info">Phone Number or Email</Label>
                  <Input
                    id="contact-info"
                    data-testid="contact-info-input"
                    required
                    value={form.contact}
                    onChange={set("contact")}
                    placeholder="(555) 123-4567 or jane@example.com"
                    className="rounded-xl h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-message">Message</Label>
                  <Textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Tell us what you're looking for — for example, a private room in Orlando, or a referral for someone you're helping."
                    className="rounded-xl min-h-[130px]"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5A5F] hover:bg-[#E0484D] disabled:opacity-60 text-white font-bold px-8 py-4 rounded-full transition-all duration-200 shadow-[0_8px_24px_rgba(255,90,95,0.35)] hover:-translate-y-0.5"
                >
                  {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
                  {submitting ? "Sending..." : "Send a Message"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
